import moment from "moment";
import { ehNfse } from "../../../utils/modeloDocumento";

const brlFormatter = new Intl.NumberFormat("pt-BR", {
  style: "currency",
  currency: "BRL",
  minimumFractionDigits: 2,
});

const intFormatter = new Intl.NumberFormat("pt-BR", {
  maximumFractionDigits: 0,
});

export const fmtBRL = (v) => brlFormatter.format(v || 0);
export const fmtInt = (v) => intFormatter.format(v || 0);

export const fmtData = (v) => (v ? moment(v).format("DD/MM/YYYY") : "");

// Data de calendário da fatura (emissão e vencimento externos, vencimento
// previsto, competência). O TMS grava meia-noite UTC nas faturas novas e 21:00
// UTC do dia anterior nas antigas (3h a menos); somar 3h em UTC devolve o dia
// certo nos dois casos. Com moment(v) o navegador (UTC-3) mostra um dia a menos.
export const dataPura = (v) => {
  if (!v) return null;
  const d = moment.utc(v).add(3, "hours");
  return d.isValid() ? moment(d.format("YYYY-MM-DD"), "YYYY-MM-DD") : null;
};
export const fmtDataPura = (v) => {
  const d = dataPura(v);
  return d ? d.format("DD/MM/YYYY") : "";
};
// Data gravada já no horário de Brasília (Dat_Pagamento): lê como está.
export const fmtDataBRT = (v) => (v ? moment.utc(v).format("DD/MM/YYYY") : "");
export const fmtDataHora = (v) => (v ? moment(v).format("DD/MM/YYYY HH:mm") : "");

// Situações da fatura (SituacaoFatura) — espelha o seed do TMS.
// PO1-3191: AVA volta a se chamar "A Validar" e entram Em Análise (9) e Em
// Tratativa (10).
// PO1-3653: a 8 (PAG) é "Integrada" (lançada no financeiro/ERP, Dat_Pagamento =
// data da integração) e entra a 11 (PGO) "Paga" (pagamento confirmado,
// Dat_PagamentoReal). Ambas encerram o fluxo.
export const SITUACOES_FATURA = [
  { value: 1, label: "A Validar", codigo: "AVA" },
  { value: 2, label: "Em Validação", codigo: "EVA" },
  { value: 3, label: "Cancelada", codigo: "CAN" },
  { value: 4, label: "Bloqueada", codigo: "BLO" },
  { value: 5, label: "Contestada", codigo: "CON" },
  { value: 6, label: "Validada", codigo: "VAL" },
  { value: 7, label: "Liberada", codigo: "LIB" },
  { value: 8, label: "Integrada", codigo: "PAG" },
  { value: 9, label: "Em Análise", codigo: "EAN" },
  { value: 10, label: "Em Tratativa", codigo: "ETR" },
  { value: 11, label: "Paga", codigo: "PGO" },
];

export const MOMENTOS = [
  { value: "PRE", label: "Pré-Fatura" },
  { value: "FAT", label: "Fatura" },
];

// Rótulo de situação exibido no HUB (controlado pelo front, não o Nome do TMS).
// Cai no fallback (nome do backend) se faltar.
export const labelSituacao = (codigo, fallback) => {
  const s = SITUACOES_FATURA.find((x) => x.codigo === codigo);
  return (s && s.label) || fallback || codigo || "—";
};

// Abas (tabfilters) da Conferência — agrupam as situações por fase/responsabilidade.
// Cada aba define o conjunto base de situações (IN no backend); a pill estreita
// para uma situação. "Todas" (situacoes vazio) = sem filtro de situação.
// O `copy` é o mini-texto exibido acima do grid conforme a aba/pill ativa.
// PO1-3191: cada card e cada pill dizem o que se espera do auditor ali.
export const ABAS_CONFERENCIA = [
  {
    key: "PROCESSAMENTO",
    label: "Em Processamento",
    situacoes: [1], // AVA
    copy:
      "Faturas recém-chegadas, aguardando a conferência automática. Nada a fazer, a menos que queira validar agora.",
    pills: [],
  },
  {
    key: "AUDITORIA",
    label: "Em Auditoria",
    situacoes: [2, 9, 10, 6], // EVA, EAN, ETR, VAL
    copy:
      "Faturas em conferência ou aguardando decisão do auditor. Comece pelas Em Análise.",
    pills: [
      {
        key: "EVA",
        label: "Em Validação",
        situacao: 2,
        copy: "A conferência automática está em andamento. Aguarde o resultado.",
      },
      {
        key: "EAN",
        label: "Em Análise",
        situacao: 9,
        copy:
          "Conferência terminou com pendência. Decida: aprovar com justificativa, devolver à transportadora ou enviar para tratativa.",
      },
      {
        key: "ETR",
        label: "Em Tratativa",
        situacao: 10,
        copy:
          "Pendência encaminhada a um responsável. Depois de resolver, clique em Revalidar.",
      },
      {
        key: "VAL",
        label: "Validada",
        situacao: 6,
        copy: "Conferida e aprovada. Libere para pagamento.",
      },
    ],
  },
  {
    key: "NEGOCIACAO",
    label: "Em Negociação",
    situacoes: [4, 5], // BLO, CON
    copy:
      "Faturas devolvidas à transportadora ou contestadas por ela, em negociação entre as partes.",
    pills: [
      {
        key: "BLO",
        label: "Bloqueada",
        situacao: 4,
        copy: "Devolvida à transportadora. Aguardando a contestação ou o cancelamento dela.",
      },
      {
        key: "CON",
        label: "Contestada",
        situacao: 5,
        copy:
          "A transportadora contestou a devolução. Decida: aprovar com justificativa ou devolver de novo.",
      },
    ],
  },
  {
    key: "FECHAMENTO",
    label: "Em Fechamento",
    situacoes: [7, 8, 11, 3], // LIB, PAG, PGO, CAN
    copy:
      "Faturas com desfecho definido: liberadas para pagamento, integradas, pagas ou canceladas.",
    pills: [
      {
        key: "LIB",
        label: "Liberada",
        situacao: 7,
        copy: "Liberada para pagamento. Integre quando o financeiro confirmar, ou estorne se precisar rever.",
      },
      {
        key: "PAG",
        label: "Integrada",
        situacao: 8,
        copy: "Integrada ao financeiro. Marque como paga quando o pagamento for confirmado.",
      },
      { key: "PGO", label: "Paga", situacao: 11, copy: "Pagamento confirmado. Nada a fazer." },
      { key: "CAN", label: "Cancelada", situacao: 3, copy: "Cancelada. Não seguirá." },
    ],
  },
  {
    key: "TODAS",
    label: "Todas",
    situacoes: [],
    copy: "Todas as faturas, sem filtro de situação.",
    pills: [],
  },
];

export const ABA_CONFERENCIA_PADRAO = "PROCESSAMENTO";

// Colunas do grid. COLUNAS_TODAS = conjunto completo (ordem padrão / aba "Todas").
// COLUNAS_POR_ABA sobrescreve a lista (e a ordem) por aba; abas sem entrada usam
// o conjunto completo. As chaves casam com o registry COLUNAS em index.js.
export const COLUNAS_TODAS = [
  "acoes",
  "momento",
  "nrExterno",
  "situacao",
  "qtdCte",
  "divergencia",
  "totalFrete",
  "totalDebito",
  "totalCredito",
  "totalFatura",
  "transportadora",
  "cteOk",
  "cteNok",
  "dtEmissaoInt",
  "dtVencPrev",
  "geradoPor",
  "embarcador",
  "remetente",
];

// Auditoria, Negociação, Fechamento e Todas compartilham as mesmas colunas.
// PO1-3191: Pendência (resumo da conferência) e Responsável.
const COLUNAS_AUDITORIA = [
  "acoes",
  "nrExterno",
  "situacao",
  "pendencia",
  "responsavel",
  "totalFatura",
  "dtEntrada",
  "dtVencimento",
  "qtdCte",
  "transportadora",
  "divergencia",
];

export const COLUNAS_POR_ABA = {
  // Em Processamento (AVA): foco no que entrou; sem OK/NOK (nada validado ainda),
  // sem divergência/valores de frete/lançamentos; mostra a Dt. Vencimento.
  PROCESSAMENTO: [
    "acoes",
    "nrExterno",
    "situacao",
    "pendencia",
    "responsavel",
    "totalFatura",
    "dtEntrada",
    "dtVencimento",
    "qtdCte",
    "transportadora",
  ],
  // Auditoria, Negociação, Fechamento e Todas: mesma lista (Divergência no final
  // p/ não comprimir Situação/Total).
  AUDITORIA: COLUNAS_AUDITORIA,
  NEGOCIACAO: COLUNAS_AUDITORIA,
  FECHAMENTO: COLUNAS_AUDITORIA,
  TODAS: COLUNAS_AUDITORIA,
};

export const colunasDaAba = (abaKey) => COLUNAS_POR_ABA[abaKey] || COLUNAS_TODAS;

// Quando o resumo lateral está aberto, o grid divide o espaço com o painel
// (360px) e não cabe todas as colunas. Encolhe para as colunas de identidade
// que cabem ao lado — interseção com as colunas da aba (preserva o que a aba
// definiu, ex.: Divergência só aparece se a aba a tiver).
export const COLUNAS_COMPACTAS = [
  "acoes",
  "nrExterno",
  "situacao",
  "pendencia",
  "responsavel",
  "divergencia",
  "totalFatura",
  "transportadora",
];

export const colunasCompactasDaAba = (abaKey) =>
  colunasDaAba(abaKey).filter((k) => COLUNAS_COMPACTAS.includes(k));

// Ações de conciliação (PO1-3191). A chave é o `acao` enviado ao backend
// (POST /faturas/conferencia/:id/conciliar). As flags dizem quais campos o
// ConciliacaoModal mostra e exige:
// - direta: executa sem modal (POST imediato, erro vira feedback na tela);
// - exigeMotivo: select de motivo (lista MOTIVOS_APROVACAO), obrigatório;
// - exigeTexto: textarea obrigatória (senão é observação opcional);
// - permiteAnexo: bloco de anexos (vai em multipart);
// - exigeResponsavel: seletor de responsável (GET .../responsaveis);
// - selecionaCtes: escolha dos CT-e em questão (idsCte), para DEVOLVER;
// - exigeDataPagamento: campo de data obrigatório, enviado como `dataPagamento`:
//   data de integração para PAGAR e data do pagamento para MARCAR_PAGA (PO1-3653).
export const ACOES_CONCILIACAO = {
  VALIDAR_AGORA: {
    label: "Validar agora",
    direta: true,
    tom: "primary",
    descricao: "Roda a conferência desta fatura agora, sem esperar o robô.",
  },
  ASSUMIR: {
    label: "Assumir",
    direta: true,
    tom: "default",
    descricao: "Passa a fatura para você.",
  },
  ENCAMINHAR: {
    label: "Encaminhar",
    exigeResponsavel: true,
    tom: "default",
    descricao: "Passa a fatura para outra pessoa, sem mudar a situação.",
  },
  APROVAR: {
    label: "Aprovar com justificativa",
    exigeMotivo: true,
    exigeTexto: true,
    permiteAnexo: true,
    tom: "success",
    descricao:
      "Aceita a pendência de frete e valida a fatura. Os CT-e marcados como não OK passam a OK.",
  },
  DEVOLVER: {
    label: "Devolver à transportadora",
    exigeTexto: true,
    selecionaCtes: true,
    permiteAnexo: true,
    tom: "danger",
    descricao:
      "Bloqueia a fatura e avisa a transportadora por e-mail, com o motivo e os CT-e em questão.",
  },
  TRATATIVA: {
    label: "Enviar para tratativa",
    exigeResponsavel: true,
    exigeTexto: true,
    tom: "info",
    descricao:
      "Encaminha a pendência a um responsável, que recebe um e-mail com a sua nota.",
  },
  LIBERAR: { label: "Liberar Fatura", tom: "success" },
  ESTORNAR: {
    label: "Estornar Liberação",
    tom: "default",
    descricao: "Volta a fatura para Em Análise e libera os CT-e.",
  },
  PAGAR: { label: "Integrar Fatura", exigeDataPagamento: true, tom: "primary" },
  MARCAR_PAGA: {
    label: "Marcar como paga",
    exigeDataPagamento: true,
    tom: "success",
    descricao: "Confirma o pagamento da fatura integrada, com a data em que foi paga.",
  },
  CONTESTAR: {
    label: "Contestar Fatura",
    exigeTexto: true,
    permiteAnexo: true,
    tom: "warning",
  },
  CANCELAR: { label: "Cancelar Fatura", tom: "danger" },
};

// Motivos do "Aprovar com justificativa" (campo `motivo` do backend).
export const MOTIVOS_APROVACAO = [
  { value: "NEGOCIACAO", label: "Negociação à parte" },
  { value: "COMPLEMENTO", label: "Complemento aprovado" },
  { value: "TABELA", label: "Tabela desatualizada" },
  { value: "EMISSAO_INCORRETA", label: "Emissão incorreta do fornecedor" },
  { value: "OUTRO", label: "Outro" },
];

// Ações que podem ser aplicadas em lote (POST /faturas/conferencia/lote).
export const ACOES_LOTE = ["APROVAR", "VALIDAR_AGORA"];

// Rótulo da ação na situação: Validar agora em Em Tratativa ou Em Análise é
// "Revalidar" (PO1-3475). Para o embarcador que recebe a fatura por e-mail
// (integracaoPorEmail, PO1-3479), "Integrar Fatura" é o envio ao financeiro.
export const ROTULO_ENVIAR_FINANCEIRO = "Enviar ao financeiro";
export const enviaFaturaPorEmail = (fatura) => Boolean(fatura && Number(fatura.integracaoPorEmail) === 1);
export const rotuloAcao = (acao, situacaoCodigo, fatura) => {
  if (acao === "VALIDAR_AGORA" && (situacaoCodigo === "ETR" || situacaoCodigo === "EAN")) return "Revalidar";
  if (acao === "PAGAR" && enviaFaturaPorEmail(fatura)) return ROTULO_ENVIAR_FINANCEIRO;
  if (acao === "TRATATIVA" && situacaoCodigo === "ETR") return "Encaminhar tratativa";
  const def = ACOES_CONCILIACAO[acao];
  return (def && def.label) || acao;
};

export const MSG_APROVAR_ESTRUTURAL =
  "Pendência estrutural (soma, total, quantidade de CT-e ou CT-e cancelado) não pode ser aprovada. Devolva à transportadora ou envie para tratativa.";

// Motivo pelo qual a ação aparece mas fica desabilitada para esta fatura, ou
// null se pode executar. Hoje só Aprovar com pendência estrutural (o backend
// devolve 409 nesse caso).
export function bloqueioAcao(acao, fatura) {
  if (acao === "APROVAR" && fatura && fatura.pendenciaEstrutural) {
    return MSG_APROVAR_ESTRUTURAL;
  }
  return null;
}

// Anexos de comprovante da decisão (PO1-2313): opcional, até 5 arquivos, 10MB cada.
export const ANEXO_MAX_ARQUIVOS = 5;
export const ANEXO_MAX_BYTES = 10 * 1024 * 1024;
export const ANEXO_EXTENSOES = ["pdf", "jpg", "jpeg", "png", "xlsx", "msg", "eml"];
export const ANEXO_ACCEPT = ANEXO_EXTENSOES.map((e) => "." + e).join(",");

// Junta arquivos novos aos já escolhidos aplicando as regras ANEXO_* (tipo,
// tamanho e quantidade). Devolve a lista aceita e a última mensagem de erro.
// Usada pelo ConciliacaoModal e pelo Contestar do MeuFrete (PO1-3222).
export function juntarAnexos(atuais, novos) {
  const combinados = (atuais || []).slice();
  let erro = null;
  for (const f of Array.from(novos || [])) {
    const ext = String(f.name || "").split(".").pop().toLowerCase();
    if (ANEXO_EXTENSOES.indexOf(ext) === -1) {
      erro = `Tipo não permitido: ${f.name}. Aceitos: ${ANEXO_EXTENSOES.join(", ")}.`;
      continue;
    }
    if (f.size > ANEXO_MAX_BYTES) {
      erro = `Arquivo acima de 10 MB: ${f.name}.`;
      continue;
    }
    if (combinados.length >= ANEXO_MAX_ARQUIVOS) {
      erro = `Máximo de ${ANEXO_MAX_ARQUIVOS} arquivos.`;
      break;
    }
    combinados.push(f);
  }
  return { arquivos: combinados, erro };
}

// Texto livre da conciliação: máximo do backend (FaturaConciliacao).
export const OBSERVACAO_MAX = 1000;

// Rótulo do campo de data das ações com exigeDataPagamento: "data de integração"
// (PAGAR) ou "data do pagamento" (MARCAR_PAGA).
export const rotuloDataPagamento = (acao) =>
  acao === "MARCAR_PAGA" ? "data do pagamento" : "data de integração";

// Valida os campos do ConciliacaoModal pelas flags da ação. Devolve a
// mensagem de erro ou null.
export function validarConciliacao(acao, campos) {
  const def = ACOES_CONCILIACAO[acao] || {};
  const c = campos || {};
  const texto = String(c.observacao || "").trim();
  if (def.exigeDataPagamento && !c.dataPagamento) return `Informe a ${rotuloDataPagamento(acao)}.`;
  if (def.exigeMotivo && !c.motivo) return "Escolha o motivo.";
  if (def.exigeResponsavel && !c.idResponsavel) return "Escolha o responsável.";
  if ((def.exigeTexto || def.exigeMotivo) && !texto) {
    return acao === "TRATATIVA" ? "Escreva a nota para o responsável." : "A justificativa é obrigatória.";
  }
  if (acao === "DEVOLVER" && /via hub$/i.test(texto)) {
    return 'Descreva o motivo da devolução (o texto não pode terminar em "via HUB").';
  }
  if (texto.length > OBSERVACAO_MAX) return `Máximo de ${OBSERVACAO_MAX} caracteres.`;
  return null;
}

// Monta o corpo do POST /conciliar: multipart quando há anexos (idsCte vira
// string JSON, como o backend espera), senão JSON.
export function montarCorpoConciliacao(acao, campos, arquivos) {
  const def = ACOES_CONCILIACAO[acao] || {};
  const c = campos || {};
  const texto = String(c.observacao || "").trim();
  const dados = { acao };
  if (texto) dados.observacao = texto;
  if (def.exigeMotivo && c.motivo) dados.motivo = c.motivo;
  if (def.exigeResponsavel && c.idResponsavel) dados.idResponsavel = c.idResponsavel;
  if (def.selecionaCtes && c.idsCte && c.idsCte.length) dados.idsCte = c.idsCte;
  if (def.exigeDataPagamento && c.dataPagamento) dados.dataPagamento = c.dataPagamento;

  const lista = arquivos || [];
  if (!lista.length) return { multipart: false, body: dados };
  const form = new FormData();
  Object.keys(dados).forEach((k) => {
    const v = dados[k];
    form.append(k, Array.isArray(v) ? JSON.stringify(v) : String(v));
  });
  lista.forEach((f) => form.append("anexos", f, f.name));
  return { multipart: true, body: form };
}

// Corpo do POST /faturas/conferencia/lote (sempre multipart).
export function montarCorpoLote(acao, idsFatura, campos, arquivos) {
  const c = campos || {};
  const form = new FormData();
  form.append("idsFatura", JSON.stringify(idsFatura || []));
  form.append("acao", acao);
  if (c.motivo) form.append("motivo", c.motivo);
  if (c.idResponsavel) form.append("idResponsavel", String(c.idResponsavel));
  if (c.dataPagamento) form.append("dataPagamento", c.dataPagamento);
  const texto = String(c.observacao || "").trim();
  if (texto) form.append("observacao", texto);
  (arquivos || []).forEach((f) => form.append("anexos", f, f.name));
  return form;
}

// Label do responsável no seletor: "nome · email", e "Everlog" para quem vem
// do EmailSustentacao do embarcador.
export function labelResponsavel(r) {
  if (!r) return "";
  const partes = [r.nome, r.email].filter(Boolean).join(" · ");
  return r.origem === "sustentacao" ? `${partes} · Everlog` : partes;
}

// Pendências da conferência (Json_PendenciasConferencia), estruturais primeiro.
export function ordenarPendencias(conferencia) {
  const lista = (conferencia && Array.isArray(conferencia.pendencias) && conferencia.pendencias) || [];
  const peso = (p) => (p && p.tipo === "estrutural" ? 0 : 1);
  return lista
    .map((p, i) => ({ p, i }))
    .sort((a, b) => peso(a.p) - peso(b.p) || a.i - b.i)
    .map((x) => x.p);
}

// Formata CNPJ (14 díg) para XX.XXX.XXX/XXXX-XX; devolve "—" se vazio.
const fmtCnpj = (v) => {
  const d = String(v || "").replace(/\D/g, "");
  if (d.length !== 14) return v || "—";
  return d.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, "$1.$2.$3/$4-$5");
};

// Rótulo amigável do tipo do documento. NFse (modelo 99, PO1-3299) não tem
// subtipo: vem antes de tudo. Para CT-e: Normal / Complementar / Anulação /
// Substituto; tipos customizados por embarcador caem no valor cru.
export const labelTipoCte = (t, modelo) => {
  if (ehNfse(modelo)) return "NFse";
  const m = {
    NORMAL: "Normal",
    COMPLEMENTAR: "Complementar",
    ANULACAO: "Anulação",
    SUBSTITUTO: "Substituto",
  };
  return m[String(t || "").toUpperCase().trim()] || t || "—";
};

// Monta um documento de FATURA DE FRETE imprimível (transportadora cobrando o
// embarcador) — não a planilha de conferência. Recebe o detalhe da fatura
// (com CNPJ/cidade da transportadora e do remetente/tomador) + a lista de CT-es.
// Aberto em nova janela e impresso via window.print() — sem dependência de PDF.
export function montarHtmlImpressao(fatura, ctes = []) {
  const f = fatura || {};
  const lista = ctes || [];
  const emissao = f.dataEmissaoExterna
    ? fmtDataPura(f.dataEmissaoExterna)
    : fmtData(f.dataEmissaoInterna);
  const vencimento = fmtDataPura(f.dataVencimentoExterna || f.dataVencimentoPrevista);
  const transpNome = f.transportadoraRazao || f.transportadoraFantasia || "—";
  const transpLoc = [f.transportadoraCidade, f.transportadoraUf].filter(Boolean).join(" / ");
  const tomadorNome = f.remetenteFantasia || f.embarcadorFantasia || "—";
  const tomadorLoc = [f.remetenteCidade, f.remetenteUf].filter(Boolean).join(" / ");

  const linhasCte = lista
    .map(
      (c) => `
      <tr>
        <td>${c.numero ?? ""}</td>
        <td style="text-align:center">${c.serie ?? ""}</td>
        <td style="text-align:center">${labelTipoCte(c.tipoCte, c.modelo)}</td>
        <td style="text-align:center">${fmtData(c.dataEmissao)}</td>
        <td style="text-align:center">${c.modal ?? ""}</td>
        <td style="text-align:right">${fmtBRL(c.freteCobrado)}</td>
      </tr>`
    )
    .join("");

  return `<!doctype html><html lang="pt-BR"><head><meta charset="utf-8">
    <title>Fatura de Prestação de Serviço de Transporte ${f.numFaturaExterna || f.id || ""}</title>
    <style>
      * { box-sizing: border-box; }
      body { font-family: Arial, Helvetica, sans-serif; font-size: 12px; color: #111; padding: 28px; }
      .doc-head { display: flex; justify-content: space-between; align-items: flex-start; border-bottom: 2px solid #111; padding-bottom: 10px; margin-bottom: 14px; }
      .doc-title { font-size: 18px; font-weight: 800; letter-spacing: .5px; }
      .doc-sub { color: #555; font-size: 11px; margin-top: 2px; }
      .doc-num { text-align: right; }
      .doc-num .n { font-size: 16px; font-weight: 800; }
      .doc-num .d { font-size: 11px; color: #333; margin-top: 3px; }
      .parts { display: flex; gap: 18px; margin-bottom: 16px; }
      .part { flex: 1; border: 1px solid #bbb; border-radius: 6px; padding: 8px 10px; }
      .part .lbl { font-size: 10px; text-transform: uppercase; letter-spacing: .06em; color: #777; font-weight: 700; }
      .part .nm { font-size: 13px; font-weight: 700; margin-top: 2px; }
      .part .ln { color: #444; margin-top: 2px; }
      table { width: 100%; border-collapse: collapse; margin-top: 4px; }
      th, td { border: 1px solid #bbb; padding: 5px 8px; }
      th { background: #f0f0f0; text-align: left; font-size: 11px; text-transform: uppercase; letter-spacing: .04em; }
      .sec-title { font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: .06em; color: #555; margin: 8px 0 4px; }
      .totais { width: 320px; margin-left: auto; margin-top: 14px; }
      .totais td { border: 0; padding: 3px 0; }
      .totais .v { text-align: right; }
      .totais .tot td { border-top: 2px solid #111; font-weight: 800; font-size: 14px; padding-top: 6px; }
      .footer { margin-top: 22px; color: #666; font-size: 10px; border-top: 1px solid #ddd; padding-top: 8px; }
    </style></head><body>
    <div class="doc-head">
      <div>
        <div class="doc-title">Fatura de Prestação de Serviço de Transporte</div>
        <div class="doc-sub">${transpNome}${f.transportadoraCnpj ? " · CNPJ " + fmtCnpj(f.transportadoraCnpj) : ""}${transpLoc ? " · " + transpLoc : ""}</div>
      </div>
      <div class="doc-num">
        <div class="n">Nº ${f.numFaturaExterna || "—"}</div>
        <div class="d">Emissão: ${emissao || "—"}</div>
        <div class="d">Vencimento: ${vencimento || "—"}</div>
      </div>
    </div>

    <div class="parts">
      <div class="part">
        <div class="lbl">Emitente (transportadora)</div>
        <div class="nm">${transpNome}</div>
        <div class="ln">CNPJ ${fmtCnpj(f.transportadoraCnpj)}</div>
        ${transpLoc ? `<div class="ln">${transpLoc}</div>` : ""}
      </div>
      <div class="part">
        <div class="lbl">Tomador</div>
        <div class="nm">${tomadorNome}</div>
        <div class="ln">CNPJ ${fmtCnpj(f.remetenteCnpj)}</div>
        ${tomadorLoc ? `<div class="ln">${tomadorLoc}</div>` : ""}
      </div>
    </div>

    <div class="sec-title">Discriminação — Conhecimentos de transporte (${fmtInt(lista.length)})</div>
    <table>
      <thead><tr>
        <th>Documento</th><th style="text-align:center">Série</th><th style="text-align:center">Tipo</th><th style="text-align:center">Emissão</th><th style="text-align:center">Modal</th><th style="text-align:right">Valor do frete</th>
      </tr></thead>
      <tbody>${linhasCte || '<tr><td colspan="6" style="text-align:center">Sem documentos.</td></tr>'}</tbody>
    </table>

    <table class="totais">
      <tr><td>Subtotal fretes</td><td class="v">${fmtBRL(f.valTotalFrete)}</td></tr>
      <tr><td>(+) Débitos</td><td class="v">${fmtBRL(f.valTotalDebito)}</td></tr>
      <tr><td>(−) Créditos</td><td class="v">${fmtBRL(f.valTotalCredito)}</td></tr>
      <tr class="tot"><td>Total da fatura</td><td class="v">${fmtBRL(f.valTotalFatura)}</td></tr>
    </table>

    <div class="footer">
      Fatura nº ${f.numFaturaExterna || "—"} (interno ${f.id ?? "—"}) · ${fmtInt(f.qtdCte || lista.length)} documento(s) · Vencimento ${vencimento || "—"}.
      Documento gerado pelo Everlog para conferência/cobrança de frete.
    </div>
  </body></html>`;
}

// Ações disponíveis por situação e papel. Espelha EXATAMENTE a máquina de
// estados da PO1-3191 (TRANSICOES do backend_hub):
//   AVA: Validar agora, Cancelar          EVA: Cancelar (robô conferindo)
//   EAN: Revalidar (VALIDAR_AGORA, PO1-3475), Aprovar, Devolver, Tratativa,
//        Assumir, Encaminhar, Cancelar
//   CON: Aprovar, Devolver, Assumir, Encaminhar, Cancelar
//   ETR: Revalidar (VALIDAR_AGORA), Encaminhar tratativa (TRATATIVA), Aprovar,
//        Devolver, Assumir, Cancelar (PO1-2256: o Revalidar só tira de ETR se
//        ficar tudo OK, então as decisões manuais valem aqui também)
//   VAL: Liberar, Cancelar                LIB: Integrar (PAGAR), Estornar
//   PAG: Marcar como paga (MARCAR_PAGA, PO1-3653)
//   BLO: transportadora Contesta ou Cancela. PGO e CAN: nenhuma.
const ACOES_EMBARCADOR = {
  AVA: ["VALIDAR_AGORA", "CANCELAR"],
  EVA: ["CANCELAR"],
  EAN: ["VALIDAR_AGORA", "APROVAR", "DEVOLVER", "TRATATIVA", "ASSUMIR", "ENCAMINHAR", "CANCELAR"],
  CON: ["APROVAR", "DEVOLVER", "ASSUMIR", "ENCAMINHAR", "CANCELAR"],
  ETR: ["VALIDAR_AGORA", "TRATATIVA", "APROVAR", "DEVOLVER", "ASSUMIR", "CANCELAR"],
  VAL: ["LIBERAR", "CANCELAR"],
  LIB: ["PAGAR", "ESTORNAR"],
  PAG: ["MARCAR_PAGA"],
};

const ACOES_TRANSPORTADORA = {
  BLO: ["CONTESTAR", "CANCELAR"],
};

export function acoesDisponiveis(situacaoCodigo, { isEmbarcador, isTransportadora } = {}) {
  const acoes = [];
  if (isEmbarcador) acoes.push(...(ACOES_EMBARCADOR[situacaoCodigo] || []));
  if (isTransportadora) {
    (ACOES_TRANSPORTADORA[situacaoCodigo] || []).forEach((a) => {
      if (!acoes.includes(a)) acoes.push(a);
    });
  }
  return acoes;
}
